import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

import '../ns-icon/ns-icon.js';

@customElement('ns-sign-button')
export class NSSignButton extends LitElement {
  @query('#button')
  private __buttonElement?: HTMLButtonElement;

  @property({ type: Boolean })
  disabled: boolean = false;

  // Shown above the button on hover or focus, and read as the button's description
  @property({ type: String })
  description: string =
    'A public ledger message will be initiated for this job. Afterwards a workflow will be created to collect the required signatures alongside the message content.';

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    css`
      :host {
        position: relative;
      }

      :host(:hover:not([data-dismissed])) .text,
      :host(:focus-within:not([data-dismissed])) .text {
        display: block;
      }

      button {
        border: 0;
        border-radius: var(--wa-border-radius-l);
        cursor: pointer;
        background: var(--wa-color-surface-lowered);
        color: var(--ns-theme-black);
        padding: var(--wa-space-2xs) var(--wa-space-xs);
        font-size: var(--wa-font-size-m);
        font-weight: 500;
        text-transform: none;
      }

      button[disabled] {
        background: var(--primary-input-color-disabled);
        cursor: not-allowed;
      }

      button img {
        width: 20px;
        margin-right: var(--wa-space-2xs);
      }

      .text {
        display: none;
        position: absolute;
        bottom: calc(100% + 4px);

        background: var(--wa-color-brand-fill-loud);
        color: var(--wa-color-brand-on-loud);

        width: 300px;

        padding: var(--wa-space-2xs) var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);

        z-index: 3;
      }

      /* Invisible hover bridge over the 4px gap down to the button, so the pointer can move onto the text */
      .text::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 100%;
        height: 4px;
      }
    `,
  ];

  // Escape hides the description while it's showing (hovered or focused); it comes back once hover and
  // focus have both left and the button is hovered or focused again.
  private readonly __dismissOnEscape = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    if (this.matches(':hover') || this.matches(':focus-within')) this.toggleAttribute('data-dismissed', true);
  };

  private readonly __resetDismissed = () => {
    setTimeout(() => {
      if (!this.matches(':hover') && !this.matches(':focus-within')) this.removeAttribute('data-dismissed');
    });
  };

  async connectedCallback() {
    document.addEventListener('keydown', this.__dismissOnEscape);
    this.addEventListener('mouseleave', this.__resetDismissed);
    this.addEventListener('focusout', this.__resetDismissed);

    super.connectedCallback();
    await this.updateComplete;

    if (!this.__buttonElement) throw new Error('Could not find button element');

    this.__buttonElement.disabled = this.disabled;
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.__dismissOnEscape);
    this.removeEventListener('mouseleave', this.__resetDismissed);
    this.removeEventListener('focusout', this.__resetDismissed);
    this.removeAttribute('data-dismissed');
    super.disconnectedCallback();
  }

  updated(changedProperties: any) {
    if (!this.__buttonElement) throw new Error('Could not find button element');
    if (changedProperties.has('disabled')) {
      this.__buttonElement.disabled = this.disabled;
    }
  }

  render() {
    return html`
      <button id="button" type="button" class="layout horizontal center" aria-describedby="sign-button-description">
        <img alt="" src="/images/icons/circle-public-ledger-key.svg" />
        <img alt="" src="/images/icons/circle-signature.svg" />
        Complete
      </button>
      <div class="text" id="sign-button-description">${this.description}</div>
    `;
  }
}
